import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { MdOutlineWarningAmber, MdErrorOutline } from "react-icons/md";
import { TbAlertCircle } from "react-icons/tb";
import { Button as ButtonLib } from "@npm_leadtech/qr-ui-components";

import { AlertContentProps } from "./types";
import { UserStatus, UserRole } from "@/types/user";
import { UserContextProps } from "@/contexts/UserContext/types";
import { SubscriptionStatus } from "@/types/subscription";
import { Typography } from "../Typography";
import { getSiteInfo } from "@/sites";
import { getAlertContent } from "./utils";
import {
  isUserFreeTrial,
  isUserNotAuthorized,
  isUserTrialExpired,
} from "@/services/UserService/UserService";
import { Link } from "../Link";
import { Alert } from "./Alert";
import { paths } from "@/utils/paths";

const BannerActionButton = ({
  label,
  loadingLabel,
  dataQA,
}: {
  label: string;
  loadingLabel?: string;
  dataQA: string;
}) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  return (
    <ButtonLib
      variant="secondary"
      size="small"
      dataQA={dataQA}
      label={isLoading && loadingLabel ? loadingLabel : label}
      isLoading={isLoading}
      onClick={() => {
        if (isLoading) return;
        setIsLoading(true);
        router.push(paths.pricing());
      }}
    />
  );
};

const isAccountValidatedAndActived = (userContext: UserContextProps, isUserValidated: boolean) => {
  let isActive = false;

  if (
    !userContext?.user?.email ||
    !isUserValidated ||
    userContext?.user?.subscription?.status === SubscriptionStatus.ACTIVE
  ) {
    isActive = true;
  }

  return isActive;
};

const shouldShowAlert = (userContext: UserContextProps, isUserValidated: boolean): boolean => {
  const { user } = userContext || {};
  const { email, subscription } = user || {};
  const hasActiveSubscription = subscription?.status === SubscriptionStatus.ACTIVE;

  return Boolean(email && isUserValidated && !hasActiveSubscription);
};

export const DashboardAlert = ({
  isUserValidated,
  userContext,
  className,
  content,
}: AlertContentProps) => {
  const userTrialActive = isUserFreeTrial(userContext) || isUserNotAuthorized(userContext);
  const userIsTrialExpired = isUserTrialExpired(userContext);

  const { showAlertForRegisteredUsers } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN!);
  const userIsRegistered =
    showAlertForRegisteredUsers &&
    userContext?.user?.userStatus?.toLowerCase() === UserStatus.REGISTERED.toLowerCase();
  const shouldShowTextAndLink = userTrialActive || userIsRegistered;

  // Role-based visibility - only ADMIN can access pricing link
  const canAccessPricing = userContext?.effectiveRole === UserRole.ADMIN;

  if (isAccountValidatedAndActived(userContext, isUserValidated)) return;

  if (!shouldShowAlert(userContext, isUserValidated)) {
    return null;
  }

  if (userIsTrialExpired) {
    const textAndButton = (
      <div className={className}>
        <div className="Alert__message">
          <MdErrorOutline aria-label="alert-icon" />
          <Typography
            Tag="p"
            classname="Alert__text"
          >
            {content.dashboardAlertTextTrialExpired}
          </Typography>
        </div>
        {canAccessPricing && (
          <div className={"Alert__btn" + (content.ctaStyle ? " rounded" : "")}>
            <BannerActionButton
              dataQA="upgrade-now-button-link"
              label={content.dashboardAlertBtnLabelTrialExpired}
              loadingLabel={content.dashboardAlertBtnLabelLoading}
            />
          </div>
        )}
      </div>
    );

    return (
      <Alert
        text={textAndButton}
        style={{ padding: "12px 16px" }}
        type="error"
      />
    );
  }

  // QRCreator & QRNow
  if (shouldShowTextAndLink) {
    const textAndLink = (
      <div className={className}>
        <Typography
          Tag="p"
          classname="Alert__text"
        >
          {`${getAlertContent(content, userContext)} `}
          {canAccessPricing && (
            <Link
              dataQA="upgrade-now-button-link"
              underlined
              className="Alert__link"
              href={"/pricing"}
              prefetch={null}
            >
              {content.dashboardAlertTextTrialActive2_1 || content.dashboardAlertBtnLabel}
            </Link>
          )}
        </Typography>
      </div>
    );

    return (
      <Alert
        icon={<MdOutlineWarningAmber aria-label="alert-icon" />}
        text={textAndLink}
        type="warning"
        closable
      />
    );
  }

  // Other domains
  const renderAlertText = (
    <div className={className}>
      <div className="Alert__message">
        <TbAlertCircle aria-label="alert-icon" />
        <Typography
          Tag="p"
          classname="Alert__text"
        >
          {`${getAlertContent(content, userContext)}`}
        </Typography>
      </div>
      {canAccessPricing && (
        <div className={"Alert__btn" + (content.ctaStyle ? " rounded" : "")}>
          <BannerActionButton
            dataQA="upgrade-now-button-link"
            label={content.dashboardAlertTextTrialActive2_1 || content.dashboardAlertBtnLabel}
            loadingLabel={content.dashboardAlertBtnLabelLoading}
          />
        </div>
      )}
    </div>
  );

  return (
    <Alert
      text={renderAlertText}
      type={isUserNotAuthorized(userContext) ? "error" : "warning"}
    />
  );
};
